import { Card, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import {
  HeaderTableCell,
  TableContainer,
  TableHeaderRow,
} from "@/components/molecules/Table/styled";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import useToast from "@/contexts/toast-context";
import { ICampaignItem } from "@/interfaces/campaign.interface";
import IProduct from "@/interfaces/product.interface";
import { CampaignFormPageMessages } from "@/messages/campaigns/campaign-form-page.messages";
import { CampaignType } from "@/types/campaign.types";

import { ItemTableRow } from "./row";
import { ITEMS_TABLE_COLUMNS } from "../../constants";
import { transformProductToItem } from "../../form-data";
import ProductAutoComplete from "../AutoComplete";

interface Props {
  items: ICampaignItem[];
  messages: CampaignFormPageMessages;
}

export function ItemsTable({ items, messages }: Props) {
  const { values, setFieldValue } = useFormikContext<CampaignFormData>();
  const { showToast } = useToast();

  const addProduct = (product: IProduct | null) => {
    if (!product) return;

    const productExists = items.some((s) => s.productCode === product.code);

    if (productExists) {
      showToast({ text: messages.productAlreadyAdded, type: "error" });
      return;
    }

    const discountPercentage =
      values.type === CampaignType.DiscountCoupon ? values.discountPercentage || 0 : 0;

    const transformedItem = transformProductToItem(product, discountPercentage);

    const updatedItems = [...items, transformedItem];
    setFieldValue("items", updatedItems);
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
            {messages.productInformationTitle}
          </MDTypography>

          {!values.disabledField && !values.isActiveStatus && (
            <ProductAutoComplete onConfirm={addProduct} messages={messages} items={items} />
          )}
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <TableContainer sx={{ marginTop: 1 }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableHeaderRow>
                    {ITEMS_TABLE_COLUMNS(messages).map((label, index) => (
                      <HeaderTableCell key={`${index} - ${label}`} width={"16%"}>
                        {label}
                      </HeaderTableCell>
                    ))}
                  </TableHeaderRow>
                </TableHead>

                <TableBody>
                  {items.length <= 0 ? (
                    <TableRow>
                      <TableCell colSpan={ITEMS_TABLE_COLUMNS(messages).length} align="center">
                        <MDTypography variant="h6" color="text" fontWeight={"regular"}>
                          {messages.noProductsAddedMessage}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, index) => (
                      <ItemTableRow
                        key={`${index} - ${item.id}`}
                        item={item}
                        index={index}
                        messages={messages}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
